import { isDevMode } from '@angular/core';
import { ActivatedRouteSnapshot, Params, PRIMARY_OUTLET, Route, Routes } from '@angular/router';
import { RouteConfig } from '@shared-features/models/route-config.model';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { RoutesConstants } from '@shared/constants/routes.constant';
import { BreadcrumbItem, BreadcrumbParentData, BreadcrumbRouteData } from '@shared/models/breadcrumb.model';

/** A `RouteConfig` whose param/query generics are irrelevant to the breadcrumb. */
type AnyRouteConfig = RouteConfig<unknown, unknown>;

/** Hard cap on visible levels. Enforced here, never in the template. */
const MAX_BREADCRUMB_LEVELS = 3;

/**
 * Cap on redirects followed while resolving one url. A misconfigured pair of routes that point at
 * each other would otherwise loop forever; three hops is far past anything the config needs.
 */
const MAX_REDIRECT_HOPS = 3;

/** Translation namespace every breadcrumb `labelKey` lives under. */
const BREADCRUMB_TRANSLATION_NAMESPACE = 'BreadCrumb';

/** Decouples the builder from `TranslateService` so it can be unit tested as a pure function. */
export type BreadcrumbTranslator = (key: string) => string;

/**
 * Whether following `url` would actually land the user on the page the level names.
 *
 * Both halves of that count: a route has to exist at the url, and the user's roles have to pass
 * whatever guard sits on it. Injected rather than computed here so the builder stays a pure
 * function, and so the caller owns the router lookups. Defaults to allowing everything.
 */
export type BreadcrumbLinkCheck = (url: string) => boolean;

/**
 * Builds the breadcrumb for the current router state.
 *
 * Pure and total: the same (route tree, url, parentId) always yields the same items, so a refresh,
 * a deep link and a browser back/forward all rebuild an identical breadcrumb with nothing persisted.
 *
 * `parentId` is the owning record's id for routes that declare a `parent` but cannot carry its id
 * in the url; the page supplies it once its record has loaded.
 */
export function buildBreadcrumbs(
  root: ActivatedRouteSnapshot,
  url: string,
  translate: BreadcrumbTranslator,
  parentId?: string | number | null,
  isReachable: BreadcrumbLinkCheck = () => true
): BreadcrumbItem[] {
  const chain = collectRouteChain(root);
  const matched = filterBreadcrumbRoutes(chain);

  if (!matched.length) {
    return [];
  }

  const params = mergeParams(chain);
  const path = stripQueryString(url);

  const routeItems = matched.map((entry, index) =>
    // the deepest match IS the current page, so its link is the URL the router reports rather
    // than one reassembled from segments.
    resolveRouteItem(entry, translate, index === matched.length - 1 ? path : entry.url)
  );

  // the owning record sits directly above the page it owns, below everything the url implies.
  const parentItem = resolveParentItem(matched[matched.length - 1].data.parent, params, translate, parentId);

  const items = [
    ...resolveAncestors(path, translate, isReachable),
    ...routeItems.slice(0, -1),
    ...(parentItem ? [parentItem] : []),
    routeItems[routeItems.length - 1],
  ];

  return markCurrent(limitBreadcrumbs(dedupeBreadcrumbs(items)));
}

/** Walks the primary outlet from the root snapshot to the leaf, at whatever depth it sits. */
function collectRouteChain(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot[] {
  const chain: ActivatedRouteSnapshot[] = [];

  for (let node = root; node; node = node.children.find((child) => child.outlet === PRIMARY_OUTLET)) {
    chain.push(node);
  }

  return chain;
}

/** A route contributes a level only by carrying breadcrumb data, so layout and lazy shells drop out. */
function filterBreadcrumbRoutes(chain: ActivatedRouteSnapshot[]): MatchedRoute[] {
  return chain
    .map((snapshot, index) => ({ snapshot, data: readBreadcrumbData(snapshot), url: buildUrlUpTo(chain, index) }))
    .filter((entry): entry is MatchedRoute => Boolean(entry.data));
}

/**
 * Resolves the levels above the matched route, from the url itself.
 *
 * The feature routing modules are flat - every page sits directly under a lazy shell - so the
 * ActivatedRoute chain alone yields a single level, and the hierarchy has to come from elsewhere.
 *
 * It deliberately does NOT come from `RouteConfig.parent`. Because the routing modules are flat,
 * every `path` has to spell out its full sub-path (`'my-customers/verify'`), which leaves `parent`
 * a hand-maintained second opinion that routinely disagrees with the url - sometimes skipping a
 * level, sometimes repeating one. Correcting it would mean shortening `path`, which would change
 * the url the route is registered at.
 *
 * Each url prefix is therefore resolved on its own. A prefix is a level exactly when the config it
 * resolves to declares a `breadcrumbLabel`, so levels are opt-in and a new route under an already
 * labelled prefix inherits its ancestors with no further wiring.
 *
 * A labelled prefix is not automatically a url the router can serve. `RoutesUtil` is a table of
 * path constants, and several sections register only their pages - `/customer-support` labels a
 * module whose routes are `tickets` and `add-ticket` and nothing at the root - so the prefix reads
 * as a section that exists while the router has nothing to put in the outlet. `isReachable` is
 * what keeps those off the screen.
 */
function resolveAncestors(path: string, translate: BreadcrumbTranslator, isReachable: BreadcrumbLinkCheck): BreadcrumbItem[] {
  const segments = toSegments(path);
  const items: BreadcrumbItem[] = [];

  // stops short of the full url: that is the current page, which comes from its own route data.
  for (let depth = 1; depth < segments.length; depth++) {
    const url = `/${segments.slice(0, depth).join('/')}`;
    const label = findRouteConfigByUrl(url)?.breadcrumbLabel;

    // a level that leads nowhere is worse than no level: it reads as a way back, and lands on
    // Access Denied or on an empty outlet. The levels that remain still describe where the page sits.
    if (label && isReachable(url)) {
      items.push({ label: translate(translationKey(label)), url, isCurrent: false });
    }
  }

  return items;
}

/**
 * Resolves the owning record's level.
 *
 * The label is static and always renders. The link is emitted only once the owner's id is known -
 * from the url when the route carries it, otherwise from the page once its record has loaded -
 * because the child's own id sits in the same position and would link to a different record.
 */
function resolveParentItem(
  parent: BreadcrumbParentData | undefined,
  params: Params,
  translate: BreadcrumbTranslator,
  parentId?: string | number | null
): BreadcrumbItem | undefined {
  if (!parent) {
    return undefined;
  }

  const id = parent.param ? params[parent.param] : parentId;
  const numericId = id === undefined || id === null || id === '' ? NaN : Number(id);
  const hasId = !Number.isNaN(numericId);
  // the owner's page may take more than its id - a contract's `:type` - so the current url's params
  // fill those, with the parent's fixed `params` over them and the owner's id last of all.
  const url = hasId ? parent.route.url({ ...params, ...parent.params, id: numericId }) : '';
  const label = translate(translationKey(parent.labelKey));

  return { label: parent.showId && hasId ? `${label} ${numericId}` : label, url, isCurrent: false };
}

function resolveRouteItem({ data, snapshot }: MatchedRoute, translate: BreadcrumbTranslator, url: string): BreadcrumbItem {
  return { label: resolveBreadcrumbLabel(data, snapshot.params, translate), url, isCurrent: false };
}

/** Translates the static label and appends the declared route param, e.g. `Shipment 45740`. */
function resolveBreadcrumbLabel(data: BreadcrumbRouteData, params: Params, translate: BreadcrumbTranslator): string {
  const label = translate(translationKey(data.labelKey));
  const value = data.param ? params[data.param] : undefined;

  return value === undefined || value === null || value === '' ? label : `${label} ${value}`;
}

/** Rebuilds the URL down to `index` from the router's own segments, never by string concatenation. */
function buildUrlUpTo(chain: ActivatedRouteSnapshot[], index: number): string {
  const segments = chain
    .slice(0, index + 1)
    .reduce<string[]>((all, snapshot) => [...all, ...snapshot.url.map((segment) => segment.path)], []);

  return `/${segments.join('/')}`;
}

/**
 * Finds the `RoutesUtil` entry the URL was matched by, preferring literal segments over params
 * the same way the router does, so `/shipments/bulk-shipments` resolves to `BulkShipments`
 * rather than to the `:type` list.
 */
function findRouteConfigByUrl(path: string): AnyRouteConfig | undefined {
  const segments = toSegments(path);
  let best: AnyRouteConfig | undefined;
  let bestScore = -1;

  for (const config of routeConfigs()) {
    const score = scoreMatch(toSegments(config.pathFromRoot), segments);

    if (score > bestScore) {
      best = config;
      bestScore = score;
    }
  }

  return best;
}

/**
 * The url the router would actually render for `url`, or `undefined` when it renders nothing.
 *
 * Answered from the router's own configuration, so it stays true for routes added later without
 * any second list to maintain. A url is served only by a route that renders something - a
 * `component`, or a `redirectTo` that hands off to one. A lazy shell that merely loads children
 * does not qualify on its own: that is exactly the case where the url resolves, the module loads,
 * and the outlet is then left empty because no child matches what is left of the url.
 *
 * A redirect is followed rather than merely accepted, and the target is what comes back. That is
 * what keeps this in step with `findRouteRoles`, which skips redirects because they carry no guard
 * of their own: a caller that checked access against the url it started from would be asking about
 * a route the user never lands on. `/invoices` redirecting to `/invoices/customer` is the live case
 * - the redirect itself is unguarded, the target is not, and a Fleet user must not be offered a
 * link that ends in Access Denied.
 */
export function resolveServedUrl(routes: Routes, url: string): string | undefined {
  return resolveSegments(routes, toSegments(url), [], {}, routes, 0);
}

/**
 * `prefix` is the url consumed by the ancestors of `routes`, which a relative `redirectTo` is
 * resolved against; `params` are the values the matched param segments took, which a `redirectTo`
 * may name; `root` is the whole config, needed to re-match a redirect target from the top.
 */
function resolveSegments(
  routes: Routes | undefined,
  segments: string[],
  prefix: string[],
  params: Params,
  root: Routes,
  hops: number
): string | undefined {
  if (hops > MAX_REDIRECT_HOPS) {
    return undefined;
  }

  for (const route of routes ?? []) {
    // `**` never matches here - `consumes` compares it literally - which is deliberate: a catch-all
    // redirect would otherwise make every url look reachable.
    if (!consumes(route, segments)) {
      continue;
    }

    const own = toSegments(route.path ?? '');
    const matched = segments.slice(0, own.length);
    const rest = segments.slice(own.length);
    const consumed = [...prefix, ...matched];
    const matchedParams = { ...params, ...paramsOf(own, matched) };
    const nested = resolveSegments(childRoutes(route), rest, consumed, matchedParams, root, hops);

    if (nested !== undefined) {
      return nested;
    }

    if (rest.length) {
      continue;
    }

    if (route.component !== undefined) {
      return toUrl(consumed);
    }

    if (route.redirectTo !== undefined) {
      return resolveSegments(root, redirectTarget(route.redirectTo, prefix, matchedParams), [], {}, root, hops + 1);
    }
  }

  return undefined;
}

/**
 * The url a `redirectTo` points at, as segments.
 *
 * `RouteConfig.url()` can carry query params into a redirect, and only the path decides what is
 * rendered. A relative target replaces the segments its own route matched, so it resolves against
 * the parent's prefix. `:name` in a target is filled from the params the match collected, the way
 * the router fills it - a name with nothing to fill it is left alone and simply fails to resolve,
 * which costs a link rather than producing a wrong one.
 */
function redirectTarget(redirectTo: string, prefix: string[], params: Params): string[] {
  const to = stripQueryString(redirectTo);
  const target = toSegments(to).map((segment) => (segment.startsWith(':') ? params[segment.slice(1)] ?? segment : segment));

  return to.startsWith('/') ? target : [...prefix, ...target];
}

/** The values a route's param segments took in the url, by name. */
function paramsOf(pattern: string[], matched: string[]): Params {
  const params: Params = {};

  pattern.forEach((part, index) => {
    if (part.startsWith(':')) {
      params[part.slice(1)] = matched[index];
    }
  });

  return params;
}

function toUrl(segments: string[]): string {
  return `/${segments.join('/')}`;
}

/**
 * The roles `PermissionGuard` would check before letting the user open `url`.
 *
 * Read out of the router's own configuration rather than from a second list, so it cannot drift
 * from what the guard actually enforces. Returns `undefined` when no route on the path declares
 * any - including when the route simply cannot be found - which callers must read as "not known
 * to be denied" rather than as "denied".
 */
export function findRouteRoles(routes: Routes, url: string): unknown[] | undefined {
  return matchRouteRoles(routes, toSegments(url));
}

function matchRouteRoles(routes: Routes | undefined, segments: string[]): unknown[] | undefined {
  for (const route of routes ?? []) {
    // a redirect carries no guard of its own; whatever it points at is matched on its own terms.
    if (route.redirectTo !== undefined || !consumes(route, segments)) {
      continue;
    }

    const rest = segments.slice(toSegments(route.path ?? '').length);
    // the deepest route that matches is the one the guard runs on, so children win over the shell.
    const nested = matchRouteRoles(childRoutes(route), rest);

    if (nested !== undefined) {
      return nested;
    }

    if (!rest.length) {
      return route.data?.roles;
    }
  }

  return undefined;
}

/**
 * A lazy module's routes only exist on the `Route` once it has loaded, and Angular keeps them on
 * an internal field. Every breadcrumb ancestor is a prefix of the current url and so lives in an
 * already-loaded module; anything else falls through to `undefined` and is treated as accessible.
 */
function childRoutes(route: Route): Routes | undefined {
  const loaded = route.children ?? (route as Route & { _loadedRoutes?: Routes })._loadedRoutes;

  if (isDevMode() && loaded === undefined && route.loadChildren) {
    console.warn(
      `[breadcrumb] no child routes for lazy route '${route.path}'. If this is not a genuinely ` +
        `unloaded module, Angular's internal '_loadedRoutes' may have been renamed and every ` +
        `level is now treated as accessible.`
    );
  }

  return loaded;
}

/**
 * Whether the route's own path matches the head of `segments`, params matching anything.
 *
 * `pathMatch: 'full'` is honoured the way the router honours it - the route then has to account
 * for every remaining segment, not just the head - so a full-match route never swallows a longer
 * url here either.
 */
function consumes(route: Route, segments: string[]): boolean {
  const pattern = toSegments(route.path ?? '');

  if (route.pathMatch === 'full' && pattern.length !== segments.length) {
    return false;
  }

  return pattern.length <= segments.length && pattern.every((part, i) => part.startsWith(':') || part === segments[i]);
}

/** Number of literally matching segments, or -1 when the pattern does not match the URL at all. */
function scoreMatch(pattern: string[], segments: string[]): number {
  if (pattern.length !== segments.length) {
    return -1;
  }

  let score = 0;

  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i].startsWith(':')) {
      continue;
    }

    if (pattern[i] !== segments[i]) {
      return -1;
    }

    score++;
  }

  return score;
}

/** Collapses consecutive levels that resolve to the same label or URL, keeping the deeper one. */
function dedupeBreadcrumbs(items: BreadcrumbItem[]): BreadcrumbItem[] {
  return items.filter((item, index) => {
    const next = items[index + 1];

    return !next || (next.label !== item.label && next.url !== item.url);
  });
}

/** The maximum-level rule: keep the last levels, which are the ones that locate the user. */
function limitBreadcrumbs(items: BreadcrumbItem[]): BreadcrumbItem[] {
  return items.slice(-MAX_BREADCRUMB_LEVELS);
}

/** Runs after the cap so the flag always lands on a visible item. */
function markCurrent(items: BreadcrumbItem[]): BreadcrumbItem[] {
  return items.map((item, index) => ({ ...item, isCurrent: index === items.length - 1 }));
}

function readBreadcrumbData(snapshot: ActivatedRouteSnapshot): BreadcrumbRouteData | undefined {
  const data = snapshot.data?.[RoutesConstants.BREADCRUMB] as BreadcrumbRouteData | undefined;

  return data?.labelKey ? data : undefined;
}

function mergeParams(chain: ActivatedRouteSnapshot[]): Params {
  return chain.reduce<Params>((all, snapshot) => ({ ...all, ...snapshot.params }), {});
}

function translationKey(labelKey: string): string {
  return `${BREADCRUMB_TRANSLATION_NAMESPACE}.${labelKey}`;
}

/** The path part of a url. Exported so callers compare urls by the same rule the builder uses. */
export function stripQueryString(url: string): string {
  return url.split('?')[0].split('#')[0];
}

function toSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

let cachedRouteConfigs: AnyRouteConfig[];

function routeConfigs(): AnyRouteConfig[] {
  if (!cachedRouteConfigs) {
    cachedRouteConfigs = Object.values(RoutesUtil).filter((value): value is AnyRouteConfig => value instanceof RouteConfig);
  }

  return cachedRouteConfigs;
}

interface MatchedRoute {
  readonly snapshot: ActivatedRouteSnapshot;
  readonly data: BreadcrumbRouteData;
  readonly url: string;
}
